// The target, its versions and the sync plan in words (projects/investing
// §3, §3.1, §5.4; D5, D11, D12).

import { formatShares, formatUsd } from "./money";

const VERSION_STATUS = {
  draft: "Draft",
  proposed: "Proposed",
  approved: "Approved",
  declined: "Declined",
  superseded: "Superseded",
};

/** A target version's status (InvestingTargetStatusEnum) in a word. */
export function versionStatusLabel(status) {
  return VERSION_STATUS[status] ?? status;
}

const KIND = {
  opportunity: "Opportunity",
  exit: "Exit",
  review: "Portfolio review",
  initial: "First target",
};

/** Why a target change was drafted (InvestingTargetKindEnum). */
export function kindLabel(kind) {
  return KIND[kind] ?? kind;
}

/** What a diff row did (InvestingTargetChangeEnum). */
export const CHANGE_LABELS = { added: "Added", removed: "Removed", resized: "Resized" };

/**
 * A D5 cap breach in plain words, from the figures the API gives (the
 * value and the limit are its strings, percentages or a count).
 */
export function breachText({ cap, subject, limit, value, holdings = [] }) {
  const who = holdings.join(", ");
  switch (cap) {
    case "max_holdings":
      return `${value} holdings — the caps allow at most ${limit}.`;
    case "add_min_pct":
      return `${who} is added at ${value}% — a new holding starts at ${limit}% or more.`;
    case "add_max_pct":
      return `${who} is added at ${value}% — a new holding starts at ${limit}% or less.`;
    case "trim_above_pct":
      return `${who} is at ${value}% — above the ${limit}% trim line.`;
    case "min_cash_pct":
      return `Cash is ${value}% — under the ${limit}% minimum.`;
    case "max_thesis_pct":
      return `The primary thesis ${subject} holds ${value}% (${who}) — over its ${limit}% cap.`;
    case "max_sector_pct":
      return `The ${subject} sector holds ${value}% (${who}) — over its ${limit}% cap.`;
    default:
      return `${cap}: ${value} against a limit of ${limit}${who ? ` (${who})` : ""}.`;
  }
}

/**
 * Where a breach's override reasons are (D5, PR 4): on every holding the
 * breach involves, or — for the two no holding carries (cash, the count) —
 * on the version. `text` is empty when none was given.
 */
export function breachOverrides(breach, version) {
  const holdings = Array.isArray(breach.holdings) ? breach.holdings : [];
  if (holdings.length === 0) return [{ symbol: null, text: version.override_reason ?? "" }];
  const listed = Array.isArray(version.holdings) ? version.holdings : [];
  return holdings.map((symbol) => ({
    symbol,
    text: listed.find((holding) => holding.symbol === symbol)?.override_reason ?? "",
  }));
}

/**
 * A draft that can't be submitted any more (D11, PR 4 review): it records
 * the approved version it was copied from, and once another is approved
 * its submit is a 409 — its author starts a new draft. `{copied, current}`
 * (version numbers, null for none), or null when it isn't stale.
 */
export function staleDraft(version, current) {
  if (version?.status !== "draft") return null;
  const copied = version.copied_from ?? null;
  const now = current?.number ?? null;
  return copied === now ? null : { copied, current: now };
}

/** "Buy" / "Sell". */
export function sideLabel(side) {
  return side === "sell" ? "Sell" : side === "buy" ? "Buy" : side;
}

/**
 * A plan order's size as the executor places it (spec §3.1): whole shares
 * at a limit, or fractional at market — in shares, or in dollars.
 */
export function orderSize(order) {
  if (order.quantity !== null && order.quantity !== undefined) {
    const shares = formatShares(order.quantity);
    const noun = shares === "1" ? "share" : "shares";
    if (order.order_type === "limit") {
      return `${shares} ${noun} at a limit of ${formatUsd(order.limit_price)}`;
    }
    return `${shares} ${noun} at market`;
  }
  return `${formatUsd(order.amount_usd)} at market`;
}

const ORDER_STATUS = {
  pending: "Pending — not placed yet",
  placed: "Placed — awaiting your approval in Robinhood",
  declined_at_broker: "Declined at broker",
  partly_filled: "Partly filled",
  filled: "Filled",
  expired: "Expired",
};

/** A plan order's status (InvestingPlanOrderStatusEnum) in words. */
export function orderStatusLabel(status) {
  return ORDER_STATUS[status] ?? status;
}

const PLAN_STATUS = {
  open: "Open — no order placed yet",
  executing: "Executing — orders are being placed and filled",
  done: "Done — every order has an outcome",
  expired: "Expired",
};

/** A sync plan's status (InvestingPlanStatusEnum) in words. */
export function planStatusLabel(status) {
  return PLAN_STATUS[status] ?? status;
}

const SKIP_REASON = {
  in_band: "within the drift band",
  under_minimum: "a trade under the minimum",
  short_of_cash: "a buy scaled under the minimum to fit the cash",
};

/** Why a plan skipped a symbol (InvestingPlanSkipReasonEnum). */
export function skipReasonLabel(reason) {
  return SKIP_REASON[reason] ?? reason;
}

const FILL_STATE = {
  queued: "Queued",
  confirmed: "Confirmed",
  partially_filled: "Partly filled",
  filled: "Filled",
  cancelled: "Cancelled",
  rejected: "Rejected",
  failed: "Failed",
};

/** A broker order's state (InvestingFillStateEnum) in a word. */
export function fillStateLabel(state) {
  return FILL_STATE[state] ?? state;
}
